import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/editorStore';
import { playbackClock } from '../playback/clock';

function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

type DragZone = 'move' | 'start' | 'end';

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;

/** Play/pause + a full-width, scrollable, zoomable caption timeline with
 *  drag-to-retime. */
export function Transport() {
  const durationMs = useEditor((s) => s.durationMs);
  const segments = useEditor((s) => s.segments);
  const beginTimingDrag = useEditor((s) => s.beginTimingDrag);
  const dragSegmentTiming = useEditor((s) => s.dragSegmentTiming);
  const hoverSegmentId = useEditor((s) => s.hoverSegmentId);
  const setHoverSegment = useEditor((s) => s.setHoverSegment);
  const [timeMs, setTimeMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let last = 0;
    return playbackClock.subscribe((t, p) => {
      setPlaying(p);
      const now = performance.now();
      if (now - last < 60) return;
      last = now;
      setTimeMs(t);
      // Keep the playhead in view while playing.
      const sc = scrollRef.current;
      const track = trackRef.current;
      if (p && sc && track && durationMs) {
        const x = (t / durationMs) * track.getBoundingClientRect().width;
        const view = sc.scrollLeft;
        if (x < view + 40 || x > view + sc.clientWidth - 40) {
          sc.scrollLeft = x - sc.clientWidth / 2;
        }
      }
    });
  }, [durationMs]);

  const pct = durationMs ? Math.min(100, (timeMs / durationMs) * 100) : 0;

  const scrub = (clientX: number) => {
    const track = trackRef.current;
    if (!track || !durationMs) return;
    const rect = track.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    playbackClock.seek(frac * durationMs);
  };

  const startSegDrag = (e: React.MouseEvent, segId: string, zone: DragZone) => {
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    const seg = segments.find((s) => s.id === segId);
    if (!track || !seg || !durationMs) return;
    const trackWidth = track.getBoundingClientRect().width;
    const origStart = seg.startMs;
    const origEnd = seg.endMs;
    const startX = e.clientX;
    beginTimingDrag();
    setDragging(true);

    const onMove = (ev: MouseEvent) => {
      const dxMs = ((ev.clientX - startX) / trackWidth) * durationMs;
      if (zone === 'move')
        dragSegmentTiming(segId, { startMs: origStart + dxMs, endMs: origEnd + dxMs });
      else if (zone === 'start') dragSegmentTiming(segId, { startMs: origStart + dxMs });
      else dragSegmentTiming(segId, { endMs: origEnd + dxMs });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const zoneAt = (e: React.MouseEvent<HTMLDivElement>): DragZone => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < 7) return 'start';
    if (x > rect.width - 7) return 'end';
    return 'move';
  };

  const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

  // Ctrl/⌘ + wheel zooms (keeping the cursor's time under the cursor); plain
  // wheel scrolls horizontally.
  const onWheel = (e: React.WheelEvent) => {
    const sc = scrollRef.current;
    if (!sc) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = sc.getBoundingClientRect();
      const cursorX = e.clientX - rect.left + sc.scrollLeft;
      const frac = cursorX / (sc.clientWidth * zoom || 1);
      const next = clampZoom(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
      setZoom(next);
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = frac * sc.clientWidth * next - (e.clientX - rect.left);
        }
      });
    } else if (e.deltaY !== 0 && sc.scrollWidth > sc.clientWidth) {
      sc.scrollLeft += e.deltaY;
    }
  };

  return (
    <div className="transport">
      <div className="transport-controls">
        <button
          className="play"
          onClick={() => playbackClock.toggle()}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? '❚❚' : '►'}
        </button>
        <span className="time">
          {fmt(timeMs)} / {fmt(durationMs)}
        </span>
        <div className="zoom">
          <button
            onClick={() => setZoom((z) => clampZoom(z / 1.5))}
            aria-label="Zoom out"
            title="Zoom out"
          >
            −
          </button>
          <span className="zoom-val">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom((z) => clampZoom(z * 1.5))}
            aria-label="Zoom in"
            title="Zoom in"
          >
            +
          </button>
          <button className="zoom-fit" onClick={() => setZoom(1)} title="Fit">
            Fit
          </button>
        </div>
      </div>

      <div className="timeline-scroll" ref={scrollRef} onWheel={onWheel}>
        <div
          className={dragging ? 'timeline dragging' : 'timeline'}
          ref={trackRef}
          style={{ width: `${zoom * 100}%` }}
          onMouseDown={(e) => scrub(e.clientX)}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={durationMs}
          aria-valuenow={timeMs}
          tabIndex={0}
        >
          {segments.map((seg) => (
            <div
              key={seg.id}
              className={seg.id === hoverSegmentId ? 'seg hover' : 'seg'}
              title="Drag to move · drag edges to trim"
              style={{
                left: `${(seg.startMs / (durationMs || 1)) * 100}%`,
                width: `${((seg.endMs - seg.startMs) / (durationMs || 1)) * 100}%`,
              }}
              onMouseDown={(e) => startSegDrag(e, seg.id, zoneAt(e))}
              onMouseEnter={() => setHoverSegment(seg.id)}
              onMouseLeave={() => setHoverSegment(null)}
              onMouseMove={(e) => {
                const z = zoneAt(e);
                e.currentTarget.style.cursor = z === 'move' ? 'grab' : 'ew-resize';
              }}
            >
              <span className="seg-h left" />
              <span className="seg-label">{seg.wordIds.length}</span>
              <span className="seg-h right" />
            </div>
          ))}
          <div className="playhead" style={{ left: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
